// Centralized capture of Chrome's beforeinstallprompt event.
//
// Why: the event can fire before React effects register listeners, causing us to
// miss the install prompt and fall back to manual instructions even though the
// browser menu offers "Install". We cache the event at module-load time and let
// UI subscribe to it.

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let registered = false;
let cachedPrompt: BeforeInstallPromptEvent | null = null;
const subscribers = new Set<(e: BeforeInstallPromptEvent | null) => void>();

function notify(e: BeforeInstallPromptEvent | null) {
  for (const cb of subscribers) {
    try {
      cb(e);
    } catch {
      // Ignore subscriber errors
    }
  }
}

function ensureRegistered(): void {
  if (registered) return;
  if (typeof window === 'undefined') return;

  registered = true;

  window.addEventListener('beforeinstallprompt', (e: Event) => {
    // Take control: Chrome won't show its own mini-infobar; we'll call prompt() on user action.
    e.preventDefault();
    cachedPrompt = e as BeforeInstallPromptEvent;
    notify(cachedPrompt);
  });

  // Clear cached prompt after installation so UI doesn't offer install again.
  window.addEventListener('appinstalled', () => {
    cachedPrompt = null;
    notify(null);
  });
}

// Register as early as possible (module-load) to avoid missing the event.
ensureRegistered();

export function getBeforeInstallPrompt(): BeforeInstallPromptEvent | null {
  ensureRegistered();
  return cachedPrompt;
}

export function subscribeBeforeInstallPrompt(
  cb: (e: BeforeInstallPromptEvent | null) => void
): () => void {
  ensureRegistered();
  subscribers.add(cb);
  // Immediately sync current state for convenience.
  cb(cachedPrompt);
  return () => {
    subscribers.delete(cb);
  };
}

